import React from 'react';

interface IndustryAdoptionVisualizationProps {
  adoptionText: string;
  className?: string;
}

interface Industry {
  name: string;
  icon: React.ReactElement;
}

const IndustryAdoptionVisualization: React.FC<IndustryAdoptionVisualizationProps> = ({ 
  adoptionText, 
  className = '' 
}) => {
  // Icon mapping for common industries
  const getIndustryIcon = (industryName: string): React.ReactElement => {
    const name = industryName.toLowerCase();
    
    // Finance
    if (name.includes('finance') || name.includes('financial') || name.includes('banking')) {
      return (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
    }
    
    // Healthcare
    if (name.includes('health') || name.includes('medical') || name.includes('life sciences')) {
      return (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
        </svg>
      );
    }
    
    // Retail / E-commerce
    if (name.includes('retail') || name.includes('e-commerce') || name.includes('ecommerce') || name.includes('consumer')) {
      return (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
        </svg>
      );
    }
    
    // Technology
    if (name.includes('technology') || name.includes('tech') || name.includes('software') || name.includes('it')) {
      return (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      );
    }
    
    // Education
    if (name.includes('education') || name.includes('learning') || name.includes('academic')) {
      return (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
        </svg>
      );
    }
    
    // Insurance
    if (name.includes('insurance')) {
      return (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      );
    }
    
    // Manufacturing
    if (name.includes('manufacturing') || name.includes('industrial') || name.includes('production')) {
      return (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
        </svg>
      );
    }
    
    // Automotive
    if (name.includes('automotive') || name.includes('automobile') || name.includes('vehicle')) {
      return (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
        </svg>
      );
    }
    
    // Government
    if (name.includes('government') || name.includes('public sector') || name.includes('govt')) {
      return (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
      );
    }
    
    // Utilities / Energy
    if (name.includes('utilities') || name.includes('energy') || name.includes('power') || name.includes('water')) {
      return (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      );
    }
    
    // Generic industry icon
    return (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
      </svg>
    );
  };

  // Parse the adoption text to extract industries
  const parseIndustries = (text: string): Industry[] => {
    const industries: Industry[] = [];
    
    // Split by newlines and match patterns like "- Industry Name"
    const lines = text.split('\n');
    
    for (const line of lines) {
      const match = line.match(/^-\s*(.+)$/);
      if (match) {
        const industryName = match[1].trim();
        
        if (industryName) {
          industries.push({
            name: industryName,
            icon: getIndustryIcon(industryName)
          });
        }
      }
    }
    
    return industries;
  };

  const industries = parseIndustries(adoptionText);

  if (industries.length === 0) {
    // Fallback to plain text if parsing fails
    return (
      <div className={`text-gray-700 leading-relaxed whitespace-pre-line ${className}`}>
        {adoptionText}
      </div>
    );
  }

  return (
    <div className={className}>
      {/* Desktop View - Grid Layout */}
      <div className="hidden md:grid md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
        {industries.map((industry, index) => (
          <div
            key={index}
            className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-200 transition-colors duration-200 hover:border-blue-300"
          >
            <div className="flex flex-col items-center text-center gap-3">
              <div className="text-blue-600">
                {industry.icon}
              </div>
              <p className="text-sm font-medium text-gray-900 leading-snug">
                {industry.name}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Mobile View - 2 Column Grid */}
      <div className="md:hidden grid grid-cols-2 gap-3">
        {industries.map((industry, index) => (
          <div
            key={index}
            className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-3 border border-blue-200 transition-colors duration-200 hover:border-blue-300"
          >
            <div className="flex flex-col items-center text-center gap-2">
              <div className="text-blue-600">
                {industry.icon}
              </div>
              <p className="text-xs font-medium text-gray-900 leading-snug">
                {industry.name}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default IndustryAdoptionVisualization;
